import {
  AuthorProfile,
  ContentEntry,
  ContentRevision,
  FinancialTerm,
  IndexabilityStatus,
  SearchPerformanceObservation,
  SeoMetadata,
  SeoOpportunity,
  SeoPageInstance,
  SitemapEntry,
  WorkflowStatus,
} from '../types';
import {
  AUTHOR_PROFILES,
  GLOSSARY_TERMS,
  METHODOLOGY_DOCS,
  PROGRAMMATIC_PAGES,
  SAMPLE_GUIDES,
  SEARCH_OBSERVATIONS,
  SEO_OPPORTUNITIES,
} from '../data/seoCmsData';

class SeoCmsEngineService {
  private guides: ContentEntry[] = [...SAMPLE_GUIDES];
  private authors: AuthorProfile[] = [...AUTHOR_PROFILES];
  private programmaticPages: SeoPageInstance[] = [...PROGRAMMATIC_PAGES];
  private searchObservations: SearchPerformanceObservation[] = [...SEARCH_OBSERVATIONS];
  private opportunities: SeoOpportunity[] = [...SEO_OPPORTUNITIES];
  private glossary: FinancialTerm[] = [...GLOSSARY_TERMS];
  private revisions: ContentRevision[] = [
    {
      id: 'rev_01',
      contentId: 'guide_california_salary_2026',
      revisionNumber: 2,
      changedBy: 'Sarah Jenkins, CPA',
      changedAt: '2026-08-15T12:00:00Z',
      changeSummary: 'Updated 2026 FTB state income tax withholding rates and SDI cap removal.',
      workflowStatus: 'PUBLISHED',
    },
    {
      id: 'rev_02',
      contentId: 'guide_us_india_remittance_2026',
      revisionNumber: 1,
      changedBy: 'Dr. Alexandre Dubois',
      changedAt: '2026-08-18T10:00:00Z',
      changeSummary: 'Initial publication with RBI master direction compliance details.',
      workflowStatus: 'PUBLISHED',
    },
  ];

  // -------------------------------------------------------------
  // EDITORIAL CMS & GUIDES
  // -------------------------------------------------------------

  public getGuides(): ContentEntry[] {
    return this.guides;
  }

  public getGuideBySlug(slug: string): ContentEntry | undefined {
    return this.guides.find((g) => g.slug === slug || g.id === slug);
  }

  public getAuthors(): AuthorProfile[] {
    return this.authors;
  }

  public getAuthorById(id: string): AuthorProfile | undefined {
    return this.authors.find((a) => a.id === id);
  }

  public getRevisionsForContent(contentId: string): ContentRevision[] {
    return this.revisions.filter((r) => r.contentId === contentId);
  }

  public updateWorkflowStatus(
    contentId: string,
    newStatus: WorkflowStatus,
    changedBy: string,
    changeSummary: string
  ): ContentEntry {
    const guideIndex = this.guides.findIndex((g) => g.id === contentId);
    if (guideIndex === -1) {
      throw new Error(`Content entry ${contentId} not found`);
    }

    const current = this.guides[guideIndex];
    const updated: ContentEntry = {
      ...current,
      status: newStatus,
      updatedAt: new Date().toISOString(),
      publishedAt: newStatus === 'PUBLISHED' ? new Date().toISOString() : current.publishedAt,
    };

    this.guides[guideIndex] = updated;

    // Log revision
    const newRevision: ContentRevision = {
      id: `rev_${Date.now()}`,
      contentId,
      revisionNumber: (this.getRevisionsForContent(contentId).length || 0) + 1,
      changedBy,
      changedAt: new Date().toISOString(),
      changeSummary,
      workflowStatus: newStatus,
    };
    this.revisions.unshift(newRevision);

    return updated;
  }

  public addGuideCorrection(
    contentId: string,
    correctedBy: string,
    previousValue: string,
    newValue: string,
    reason: string
  ): ContentEntry {
    const guideIndex = this.guides.findIndex((g) => g.id === contentId);
    if (guideIndex === -1) throw new Error('Guide not found');

    const guide = this.guides[guideIndex];
    const newCorrection = {
      id: `corr_${Date.now()}`,
      contentId,
      correctedAt: new Date().toISOString(),
      correctedBy,
      previousValue,
      newValue,
      reason,
      isVisibleNote: true,
    };

    const updated = {
      ...guide,
      corrections: [newCorrection, ...guide.corrections],
      updatedAt: new Date().toISOString(),
    };

    this.guides[guideIndex] = updated;
    return updated;
  }

  public createGuideDraft(
    title: string,
    summary: string,
    bodyMarkdown: string,
    authorId: string,
    topicCluster: string,
    riskLevel: ContentEntry['riskLevel'] = 'MODERATE'
  ): ContentEntry {
    const id = `guide_${Date.now()}`;
    const slug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    const author = this.getAuthorById(authorId);
    const authorName = author ? author.name : 'Editorial Team';

    const newGuide: ContentEntry = {
      id,
      contentType: 'GUIDE',
      slug,
      locale: 'en',
      status: 'DRAFT',
      title,
      summary,
      bodyMarkdown,
      authorId,
      updatedAt: new Date().toISOString(),
      riskLevel,
      sources: [],
      corrections: [],
      topicCluster,
      seoMetadata: {
        title: `${title} — YamooPay`,
        description: summary.substring(0, 155),
        canonicalUrl: `https://yamoopay.com/en/guides/${slug}`,
        robotsDirective: 'noindex, follow',
        hreflangMap: {
          'en-US': `https://yamoopay.com/en/guides/${slug}`,
          'x-default': `https://yamoopay.com/en/guides/${slug}`,
        },
      },
    };

    this.guides.unshift(newGuide);

    this.revisions.unshift({
      id: `rev_${Date.now()}`,
      contentId: id,
      revisionNumber: 1,
      changedBy: authorName,
      changedAt: new Date().toISOString(),
      changeSummary: 'Created initial draft',
      workflowStatus: 'DRAFT',
    });

    return newGuide;
  }

  // -------------------------------------------------------------
  // PROGRAMMATIC SEO & QUALITY GATE
  // -------------------------------------------------------------

  public getProgrammaticPages(): SeoPageInstance[] {
    return this.programmaticPages;
  }

  public evaluateIndexability(page: Partial<SeoPageInstance>): {
    indexable: boolean;
    reason: string;
    status: IndexabilityStatus;
  } {
    const dataScore = page.dataReadinessScore ?? 0;
    const uniqScore = page.uniquenessScore ?? 0;

    if (dataScore < 70) {
      return {
        indexable: false,
        reason: `Quality Gate Failed: Data readiness score (${dataScore}/100) below minimum threshold (70/100).`,
        status: 'QUALITY_NOINDEX',
      };
    }

    if (uniqScore < 60) {
      return {
        indexable: false,
        reason: `Quality Gate Failed: Uniqueness score (${uniqScore}/100) too low. Potential scaled content duplicate.`,
        status: 'QUALITY_NOINDEX',
      };
    }

    return {
      indexable: true,
      reason: 'Quality Gate Passed: High data readiness and unique financial value confirmed.',
      status: 'INDEXABLE',
    };
  }

  // -------------------------------------------------------------
  // SEARCH CONSOLE INTELLIGENCE
  // -------------------------------------------------------------

  public getSearchObservations(): SearchPerformanceObservation[] {
    return this.searchObservations;
  }

  public getOpportunities(): SeoOpportunity[] {
    return this.opportunities;
  }

  public getSearchPerformanceSummary() {
    const totalClicks = this.searchObservations.reduce((acc, curr) => acc + curr.clicks, 0);
    const totalImpressions = this.searchObservations.reduce((acc, curr) => acc + curr.impressions, 0);
    const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const avgPosition =
      this.searchObservations.length > 0
        ? this.searchObservations.reduce((acc, curr) => acc + curr.position, 0) /
          this.searchObservations.length
        : 0;

    const indexedCount = this.programmaticPages.filter((p) => p.indexabilityStatus === 'INDEXABLE').length;
    const noindexCount = this.programmaticPages.filter((p) => p.indexabilityStatus !== 'INDEXABLE').length;

    return {
      totalClicks,
      totalImpressions,
      avgCtr: Number(avgCtr.toFixed(2)),
      avgPosition: Number(avgPosition.toFixed(1)),
      indexedCount,
      noindexCount,
      openOpportunitiesCount: this.opportunities.filter((o) => o.status === 'OPEN').length,
    };
  }

  // -------------------------------------------------------------
  // SITEMAP ENGINE
  // -------------------------------------------------------------

  public generateSitemapEntries(): SitemapEntry[] {
    const entries: SitemapEntry[] = [];

    // Add guides
    this.guides
      .filter((g) => g.status === 'PUBLISHED')
      .forEach((g) => {
        entries.push({
          loc: g.seoMetadata.canonicalUrl,
          lastmod: g.updatedAt.split('T')[0],
          changefreq: 'weekly',
          priority: 0.8,
          locale: g.locale,
          alternateHreflangs: Object.entries(g.seoMetadata.hreflangMap).map(([lang, href]) => ({
            lang,
            href,
          })),
        });
      });

    // Add programmatic indexable pages
    this.programmaticPages
      .filter((p) => p.indexabilityStatus === 'INDEXABLE')
      .forEach((p) => {
        entries.push({
          loc: p.canonicalUrl,
          lastmod: p.lastSignificantUpdate.split('T')[0],
          changefreq: 'daily',
          priority: 0.9,
          locale: p.locale,
          alternateHreflangs: Object.entries(p.hreflangGroup).map(([lang, href]) => ({
            lang,
            href,
          })),
        });
      });

    return entries;
  }

  // -------------------------------------------------------------
  // STRUCTURED DATA / SCHEMA.ORG GENERATOR
  // -------------------------------------------------------------

  public generateJsonLdForGuide(guide: ContentEntry) {
    const author = this.getAuthorById(guide.authorId);
    const reviewer = guide.reviewerId ? this.getAuthorById(guide.reviewerId) : null;

    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: guide.title,
      description: guide.seoMetadata.description,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': guide.seoMetadata.canonicalUrl,
      },
      datePublished: guide.publishedAt,
      dateModified: guide.updatedAt,
      author: author
        ? {
            '@type': 'Person',
            name: author.name,
            jobTitle: author.role,
            url: `https://yamoopay.com/authors/${author.slug}`,
          }
        : {
            '@type': 'Organization',
            name: 'YamooPay Editorial Board',
          },
      ...(reviewer && {
        reviewedBy: {
          '@type': 'Person',
          name: reviewer.name,
          jobTitle: reviewer.role,
        },
      }),
      publisher: {
        '@type': 'Organization',
        name: 'YamooPay',
        url: 'https://yamoopay.com',
        logo: {
          '@type': 'ImageObject',
          url: 'https://yamoopay.com/logo.png',
        },
      },
    };
  }

  // -------------------------------------------------------------
  // GLOSSARY & METHODOLOGY
  // -------------------------------------------------------------

  public getGlossaryTerms(): FinancialTerm[] {
    return this.glossary;
  }

  public getMethodologyDocs() {
    return METHODOLOGY_DOCS;
  }
}

export const seoCmsEngine = new SeoCmsEngineService();
